import { select, takeEvery, put, all, call, take } from 'redux-saga/effects'
import { isNil } from 'ramda'
import { INTERNAL } from 'constants/featureTypes'
import { DEFAULT_PRIORITY } from 'constants/featureDefaults'

import {
  UPDATE_XMC_MODULE_NAME,
  SET_FEATURE_FIELD,
  setFeatureField,
} from 'actions/feature/featureMetadata'
import { setOutputField } from 'actions/feature/outputs'
import { setQueryField } from 'actions/feature/queries'

import {
  getFeatureModuleName,
  getFeatureMetadata,
} from 'selectors/feature/featureMetadata'
import {
  getCanOutputs,
  getOutputPinUris,
  getMappedNativeOutputs,
} from 'selectors/feature/outputs'
import { getSignalUris } from 'selectors/feature/inputs'
import { getSetSimpleConditions } from 'selectors/feature/conditions'

import { canOutputId } from 'components/feature/utils/outputs'

export function* watchUpdateModuleName() {
  yield takeEvery(UPDATE_XMC_MODULE_NAME, updateModuleName)
}

function* updateModuleName() {
  const featureModuleName = yield select(getFeatureModuleName)

  const canOutputs = yield select(getCanOutputs)
  yield all(
    canOutputs.map(({ numberOfBits, id }) =>
      put(
        setOutputField(
          'mappedOutputId',
          canOutputId(featureModuleName, numberOfBits),
          id
        )
      )
    )
  )

  // reset all signals that are not available for new module
  const setSimpleConditions = yield select(getSetSimpleConditions)
  const signalsForModule = yield select(getSignalUris)

  yield all(
    Object.values(setSimpleConditions).reduce((acc, condition) => {
      const { signal, uri } = condition
      if (signal && !signalsForModule.includes(signal)) {
        return [...acc, put(setQueryField(uri, 'signal', null))]
      }
      return acc
    }, [])
  )

  // reset all output pins that are not available for new module
  const mappedNativeOutputs = yield select(getMappedNativeOutputs)
  const pinsForModule = yield select(getOutputPinUris)

  yield all(
    Object.values(mappedNativeOutputs).reduce((acc, output) => {
      const { mappedOutputId, id } = output
      if (mappedOutputId && !pinsForModule.includes(mappedOutputId)) {
        return [...acc, put(setOutputField('mappedOutputId', null, id))]
      }
      return acc
    }, [])
  )
}

export function* watchFeatureFieldUpdate() {
  const handlers = {
    type: updateTypeSideEffect,
  }

  while (true) {
    const { paramName, value, featureId } = yield take(SET_FEATURE_FIELD)
    if (handlers[paramName]) {
      const output = yield select(getFeatureMetadata)
      const setField = (type, val) => put(setFeatureField(type, val, featureId))

      yield call(handlers[paramName], {
        value,
        featureId,
        output,
        setField,
      })
    }
  }
}

function* updateTypeSideEffect({ output, setField }) {
  const actions = []
  const { type, priority, releaseNumber } = output
  if (type === INTERNAL) {
    if (isNil(priority)) {
      actions.push(setField('priority', DEFAULT_PRIORITY))
    }
    if (isNil(releaseNumber)) {
      actions.push(setField('releaseNumber', ''))
    }
  }

  yield all(actions)
}
